import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  Card,
  Flex,
  Group,
  SegmentedControl,
  Text,
  TextInput,
  Stack,
  Badge,
} from "@mantine/core";
import {
  IconCalendar,
  IconDeviceMobile,
  IconHeartbeat,
  IconInbox,
  IconMail,
  IconBell,
  IconMessage,
} from "@tabler/icons-react";

import { PaginatedList } from "@components/shared";
import entityIcon from "@util/entityIcon";
import entityColor from "@util/entityColor";

export default function BulkCommunicationList({
  requestInfo = {
    url: `/retrieve-bulk-communicators/`,
    data: {},
  },
  locationId,
  orgId,
  effortId,
  colorBackground = false,
}) {
  const [filters, setFilters] = useState({
    disposition: "everything",
    variety: "entire",
  });
  const [searchValue, setSearchValue] = useState("");

  return (
    <div>
      <TextInput
        placeholder="Search..."
        size="lg"
        value={searchValue}
        onChange={(e) => setSearchValue(e.target.value)}
        mb="sm"
      />
      <Flex gap="sm" direction={{ base: "column", sm: "row" }} wrap="wrap">
        <SegmentedControl
          value={filters.variety}
          data={[
            { label: "All", value: "entire" },
            { label: "Email", value: "email" },
            { label: "SMS", value: "sms" },
            // { label: "Alert", value: "alert" },
          ]}
          onChange={(e) =>
            setFilters({
              ...filters,
              variety: e,
            })
          }
        />
        <SegmentedControl
          value={filters.disposition}
          data={[
            { label: "All", value: "everything" },
            { label: "Waiting", value: "waiting" },
            { label: "Enqueued", value: "enqueued" },
            { label: "Scheduled", value: "scheduled" },
            { label: "In Progress", value: "in_progress" },
            { label: "Completed", value: "completed" },
          ]}
          onChange={(e) =>
            setFilters({
              ...filters,
              disposition: e,
            })
          }
        />
      </Flex>
      <PaginatedList
        requestInfo={{
          url: requestInfo.url,
          data: {
            ...requestInfo.data,
            ...filters,
            location_id: locationId,
            organization_id: orgId,
            search_value: searchValue,
            campaign_effort_id: effortId,
          },
        }}
        responseMapping={(r) =>
          r.map((m) => ({
            ...m,
            link_url: `/bulk-communication/${m.id}`,
          }))
        }
        displayFormatter={({ original }) => (
          <Item key={original.id} original={original} />
        )}
      />
    </div>
  );
}

export const Item = ({ original, colorBackground = false }) => {
  return (
    <Card
      key={original.id}
      style={{
        background: colorBackground
          ? `var(--mantine-color-${entityColor.send}-0)`
          : "white",
      }}
    >
      <Stack gap={5}>
        <Flex
          gap="xs"
          direction={{ base: "column", sm: "row" }}
          align={{ base: "flex-start", sm: "center" }}
        >
          <Text fw={700} component={Link} to={original.link_url} lineClamp={1}>
            {original.title}
          </Text>
          {original.variety && (
            <Badge
              size="sm"
              color="gray"
              variant="light"
              leftSection={
                original.variety === 1 ? (
                  <IconMail size={16} />
                ) : original.variety === 2 ? (
                  <IconDeviceMobile size={16} />
                ) : (
                  <IconBell size={16} />
                )
              }
            >
              {original.variety_formatted} Sender
            </Badge>
          )}
        </Flex>
        {original.subject && (
          <Flex gap="xs" align="center">
            <>
              <IconInbox size={16} />
            </>
            <Text size="sm">Subject: {original.subject}</Text>
          </Flex>
        )}
        {(original.location_name || original.organization_name) && (
          <Flex gap="xs" align="center">
            <>
              {original.location_id
                ? entityIcon.location()
                : entityIcon.organization()}
            </>
            <Text size="sm">
              {original.location_name
                ? original.location_name
                : original.organization_name}
            </Text>
          </Flex>
        )}
        {original.campaign_effort_title && (
          <Flex gap="xs" align="center">
            <>{entityIcon.effort()}</>
            <Text size="sm">{original.campaign_effort_title}</Text>
          </Flex>
        )}
        {original.disposition_formatted && (
          <Flex gap="xs" align="center">
            <>
              <IconHeartbeat size={16} />
            </>
            <Text size="sm" style={{ textTransform: "capitalize" }}>
              {original.disposition_formatted}
            </Text>
          </Flex>
        )}
        {original.scheduled_at && (
          <Flex gap="xs" align="center">
            <>
              <IconCalendar size={16} />
            </>
            <Text size="sm">
              scheduled for {new Date(original.scheduled_at).toLocaleString()}
            </Text>
          </Flex>
        )}
        {original.bulk_communicator_messages_count > 0 && (
          <Flex gap="xs" align="center">
            <>
              <IconMessage size={16} />
            </>
            <Text size="sm">
              {original.bulk_communicator_messages_count} message
              {original.bulk_communicator_messages_count === 1 ? "" : "s"}
            </Text>
          </Flex>
        )}
      </Stack>
    </Card>
  );
};
